import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SERVER_NAME } from './config';
import SignalChip from './intelligence/SignalChip';
import { createPanelStyle, intelligencePalette } from './intelligence/intelligenceTheme';
import { logEvent, logPageView } from './analytics';
import {
  mockActivity,
  mockAttribution,
  mockBlockedActions,
  mockLifecycle,
  mockSummary,
} from './intelligence/mockIntelligenceConsoleData';

const PERIODS = ['7d', '30d', '90d', 'ytd', 'all'];
const GROUPS = [
  { label: 'By Token', value: 'token' },
  { label: 'By Agent', value: 'agent' },
  { label: 'By Thesis', value: 'thesis' },
  { label: 'By Story Type', value: 'storyType' },
  { label: 'By Risk Regime', value: 'riskRegime' },
];

const PERIOD_LABELS = {
  '7d': 'last 7 days',
  '30d': 'last 30 days',
  '90d': 'last 90 days',
  ytd: 'year to date',
  all: 'all available history',
};

const KPI_DESCRIPTIONS = {
  'Paper Portfolio Value': 'Current simulated portfolio equity. This is paper-trading performance, not live custody.',
  'Total Agent P/L': 'All-time simulated profit and loss attributed to agent decisions.',
  'P/L': 'Profit and loss for the selected date range.',
  'Benchmark Delta': 'Agent return minus benchmark return over the selected date range.',
  'Win Rate': 'Percent of closed evaluated agent actions with positive outcomes.',
  'Open Positions': 'Paper positions currently open or monitored by agents.',
  'Blocked Actions': 'Actions stopped by risk, admissibility, cash, liquidity, or confidence checks.',
  'Current Risk Mode': 'Current system posture inferred from risk controls and market conditions.',
};

const FALLBACK_PERIOD_FACTORS = {
  '7d': { pnlUsd: -740, pnlPct: -0.6, delta: 0.9, winRate: 54, points: 7 },
  '30d': { pnlUsd: -3210, pnlPct: -2.4, delta: 2.7, winRate: 58, points: 30 },
  '90d': { pnlUsd: 8420, pnlPct: 7.4, delta: 5.8, winRate: 61, points: 45 },
  ytd: { pnlUsd: 18440, pnlPct: 16.8, delta: 8.6, winRate: 59, points: 60 },
  all: { pnlUsd: 28420, pnlPct: 28.4, delta: 12.1, winRate: 58, points: 72 },
};

function emptyPerformancePayload(period = '30d') {
  return { period, series: [], markers: [] };
}

function fmtUsd(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
}

function buildFallbackPayload(period, groupBy) {
  const factors = FALLBACK_PERIOD_FACTORS[period] || FALLBACK_PERIOD_FACTORS['30d'];
  const periodLabel = PERIOD_LABELS[period] || period;
  return {
    summary: {
      ...mockSummary,
      period,
      periodPnlUsd: factors.pnlUsd,
      periodPnlPct: factors.pnlPct,
      benchmarkDeltaPct: factors.delta,
      winRatePct: factors.winRate,
      verdictText: `Agent paper portfolio is ${factors.pnlPct >= 0 ? 'up' : 'down'} ${Math.abs(factors.pnlPct).toFixed(1)}% over ${periodLabel} and ${factors.delta >= 0 ? 'outperforming' : 'underperforming'} ETH by ${Math.abs(factors.delta).toFixed(1)}%. Current mode: selective.`,
    },
    performance: emptyPerformancePayload(period),
    lifecycle: { ...mockLifecycle, period },
    attribution: { ...mockAttribution, period, groupBy },
    blockedActions: { ...mockBlockedActions, period },
    activity: mockActivity,
    demoMode: true,
  };
}

function fmtTime(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function panel(extra = {}) {
  return createPanelStyle({ padding: 18, ...extra });
}

function EmptyState({ label }) {
  return <div style={{ color: intelligencePalette.muted, fontSize: 13, padding: '14px 0' }}>{label}</div>;
}

function TimeRangeSelector({ period, onChange, loading }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {PERIODS.map((option) => (
        <button
          key={option}
          type="button"
          title={`Show Intelligence Console metrics for ${PERIOD_LABELS[option] || option}`}
          aria-pressed={period === option}
          onClick={() => onChange(option)}
          style={{
            border: `1px solid ${period === option ? intelligencePalette.borderStrong : intelligencePalette.border}`,
            background: period === option ? 'rgba(77,121,255,0.28)' : 'rgba(255,255,255,0.03)',
            color: period === option ? intelligencePalette.text : intelligencePalette.muted,
            borderRadius: 999,
            padding: '7px 12px',
            fontWeight: 800,
            fontSize: 12,
            cursor: 'pointer',
            textTransform: 'uppercase',
            boxShadow: period === option ? '0 0 0 1px rgba(77,121,255,0.16)' : 'none',
            opacity: loading && period === option ? 0.7 : 1,
          }}
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function TruthPanel({ summary }) {
  const riskTone = String(summary.riskMode || '').toUpperCase() === 'DEFENSIVE' ? 'weak' : 'watch';
  return (
    <section style={panel({ display: 'grid', gap: 14 })}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <div>
          <div style={{ color: intelligencePalette.muted, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 }}>
            System Verdict
          </div>
          <div style={{ fontSize: 20, lineHeight: 1.35, fontWeight: 850, color: intelligencePalette.text, maxWidth: 820 }}>
            {summary.verdictText || 'Agent paper portfolio has insufficient data for a verdict yet.'}
          </div>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
        <TruthFact label="Current mode" value={summary.riskMode || '—'} tone={riskTone} />
        <TruthFact label="Largest thesis" value={summary.largestThesis || '—'} />
        <TruthFact label="Open positions" value={summary.openPositionsCount ?? '—'} />
        <TruthFact label="Primary blocked reason" value={summary.primaryBlockedReason || '—'} tone="decaying" />
      </div>
    </section>
  );
}

function TruthFact({ label, value, tone }) {
  return (
    <div style={{ border: `1px solid ${intelligencePalette.border}`, borderRadius: 14, padding: 12, background: 'rgba(255,255,255,0.025)' }}>
      <div style={{ color: intelligencePalette.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
      <div style={{ color: intelligencePalette.text, fontWeight: 800, marginTop: 6, wordBreak: 'break-word' }}>
        {tone ? <SignalChip label={value} tone={tone} /> : value}
      </div>
    </div>
  );
}

function KpiGrid({ summary, period, navigate }) {
  const cards = [
    { label: 'Paper Portfolio Value', value: fmtUsd(summary.currentEquityUsd), sub: 'Simulated portfolio', path: '/agent-verdicts' },
    { label: 'Total Agent P/L', value: fmtUsd(summary.totalPnlUsd), positive: Number(summary.totalPnlUsd) >= 0, path: '/agent-verdicts' },
    { label: `${period.toUpperCase()} P/L`, value: `${fmtUsd(summary.periodPnlUsd)} · ${fmtPct(summary.periodPnlPct)}`, positive: Number(summary.periodPnlUsd) >= 0, path: '/agent-verdicts' },
    { label: 'Benchmark Delta', value: `${fmtPct(summary.benchmarkDeltaPct)} vs ETH`, positive: Number(summary.benchmarkDeltaPct) >= 0 },
    { label: 'Win Rate', value: fmtPct(summary.winRatePct), path: '/agent-verdicts' },
    { label: 'Open Positions', value: summary.openPositionsCount ?? '—', path: '/agent-verdicts' },
    { label: 'Blocked Actions', value: summary.blockedActionsCount ?? '—' },
    { label: 'Current Risk Mode', value: summary.riskMode || '—' },
  ];
  return (
    <section style={panel()}>
      <div style={{ fontSize: 19, fontWeight: 850, marginBottom: 6 }}>Portfolio Scorecard</div>
      <div style={{ color: intelligencePalette.muted, fontSize: 13, marginBottom: 14 }}>Paper portfolio, benchmark, risk, and action-control metrics for the selected range.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, minmax(128px, 1fr))', gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
        {cards.map((card) => (
          <button
            key={card.label}
            type="button"
            title={KPI_DESCRIPTIONS[card.label] || KPI_DESCRIPTIONS['P/L']}
            onClick={() => { if (card.path) navigate(card.path); }}
            style={{
              border: `1px solid ${card.positive == null ? intelligencePalette.border : card.positive ? 'rgba(158,247,197,0.32)' : 'rgba(255,122,138,0.32)'}`,
              background: 'rgba(255,255,255,0.025)',
              color: intelligencePalette.text,
              borderRadius: 14,
              padding: 10,
              minHeight: 68,
              minWidth: 0,
              cursor: card.path ? 'pointer' : 'default',
              textAlign: 'left',
            }}
          >
            <div style={{ color: intelligencePalette.muted, fontSize: 9, marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.label}</div>
            <div style={{ color: card.positive == null ? intelligencePalette.text : card.positive ? intelligencePalette.positive : intelligencePalette.danger, fontSize: 14, fontWeight: 850, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {card.value}
            </div>
            {card.sub ? <div style={{ color: intelligencePalette.muted, fontSize: 9, marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{card.sub}</div> : null}
          </button>
        ))}
      </div>
    </section>
  );
}

function PerformanceChart({ data, period, onPeriodChange, loading, onMarkerClick }) {
  const [displayMode, setDisplayMode] = useState('usd');
  const series = Array.isArray(data.series) ? data.series : [];
  const markers = Array.isArray(data.markers) ? data.markers : [];
  const width = 920;
  const height = 260;
  const padX = 42;
  const padY = 26;
  const firstEquity = Number(series[0]?.equityUsd);
  const values = series.map((row) => {
    const equity = Number(row.equityUsd);
    if (displayMode === 'pct') {
      return Number.isFinite(equity) && Number.isFinite(firstEquity) && firstEquity !== 0 ? ((equity - firstEquity) / firstEquity) * 100 : null;
    }
    return equity;
  }).filter(Number.isFinite);
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const span = Math.max(1, max - min);
  const x = (i) => padX + (series.length <= 1 ? 0 : (i / (series.length - 1)) * (width - padX * 2));
  const y = (v) => height - padY - ((Number(v) - min) / span) * (height - padY * 2);
  const valueFor = (row) => {
    const equity = Number(row.equityUsd);
    if (displayMode === 'pct') {
      return Number.isFinite(equity) && Number.isFinite(firstEquity) && firstEquity !== 0 ? ((equity - firstEquity) / firstEquity) * 100 : null;
    }
    return equity;
  };
  const chartPoints = series.map((row, i) => ({ row, value: valueFor(row), x: x(i) })).filter((point) => Number.isFinite(point.value));
  const linePath = chartPoints.map((point, i) => `${i === 0 ? 'M' : 'L'} ${point.x} ${y(point.value)}`).join(' ');
  const areaPath = chartPoints.length ? `${linePath} L ${chartPoints[chartPoints.length - 1].x} ${height - padY} L ${chartPoints[0].x} ${height - padY} Z` : '';
  const currentValue = chartPoints[chartPoints.length - 1]?.value;
  const startValue = chartPoints[0]?.value;
  const change = Number.isFinite(currentValue) && Number.isFinite(startValue) ? currentValue - startValue : null;
  const positive = Number(change) >= 0;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => min + span * ratio);
  const formatChartValue = (value) => displayMode === 'pct' ? fmtPct(value) : fmtUsd(value);
  return (
    <section style={panel()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 850 }}>E3D Trading App Performance</div>
          <div style={{ color: intelligencePalette.muted, fontSize: 13, lineHeight: 1.5, maxWidth: 720 }}>
            TradingView-style equity curve for the E3D paper trading app over {PERIOD_LABELS[period] || period}. Use $ for portfolio value or % for return over the selected range.
          </div>
        </div>
        <div style={{ display: 'grid', gap: 10, justifyItems: 'end' }}>
          <TimeRangeSelector period={period} onChange={onPeriodChange} loading={loading} />
          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
            {[
              ['usd', '$'],
              ['pct', '%'],
            ].map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                aria-pressed={displayMode === mode}
                onClick={() => setDisplayMode(mode)}
                style={{
                  border: `1px solid ${displayMode === mode ? intelligencePalette.borderStrong : intelligencePalette.border}`,
                  background: displayMode === mode ? 'rgba(77,121,255,0.28)' : 'rgba(255,255,255,0.03)',
                  color: displayMode === mode ? intelligencePalette.text : intelligencePalette.muted,
                  borderRadius: 10,
                  padding: '6px 10px',
                  fontSize: 12,
                  fontWeight: 850,
                  cursor: 'pointer',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      {loading ? (
        <EmptyState label="Loading E3D performance data…" />
      ) : !series.length ? (
        <EmptyState label="Waiting for E3D performance data." />
      ) : (
        <div style={{ border: `1px solid ${intelligencePalette.border}`, borderRadius: 16, overflow: 'hidden', background: 'linear-gradient(180deg, rgba(8,13,28,0.98), rgba(6,9,18,0.98))' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '12px 14px 0', flexWrap: 'wrap' }}>
            <div>
              <div style={{ color: intelligencePalette.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>E3D paper equity</div>
              <div style={{ fontSize: 18, fontWeight: 850, color: intelligencePalette.text }}>{formatChartValue(currentValue)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: intelligencePalette.muted, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Range change</div>
              <div style={{ fontSize: 18, fontWeight: 850, color: positive ? intelligencePalette.positive : intelligencePalette.danger }}>{formatChartValue(change)}</div>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <svg width="100%" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="E3D trading app performance chart">
              <defs>
                <linearGradient id="e3dPerformanceFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor={positive ? intelligencePalette.positive : intelligencePalette.danger} stopOpacity="0.28" />
                  <stop offset="100%" stopColor={positive ? intelligencePalette.positive : intelligencePalette.danger} stopOpacity="0.02" />
                </linearGradient>
              </defs>
              {yTicks.map((tick) => {
                const tickY = y(tick);
                return (
                  <g key={tick}>
                    <line x1={padX} x2={width - padX} y1={tickY} y2={tickY} stroke="rgba(255,255,255,0.07)" />
                    <text x={width - padX + 8} y={tickY + 4} fill={intelligencePalette.muted} fontSize="10">{formatChartValue(tick)}</text>
                  </g>
                );
              })}
              <path d={areaPath} fill="url(#e3dPerformanceFill)" />
              <path d={linePath} fill="none" stroke={positive ? intelligencePalette.positive : intelligencePalette.danger} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
              {markers.slice(0, 20).map((marker) => {
                const idx = Math.max(0, Math.min(series.length - 1, series.findIndex((row) => new Date(row.time) >= new Date(marker.time))));
                const point = chartPoints.find((item) => item.row === series[idx < 0 ? 0 : idx]);
                if (!point) return null;
                return (
                  <g key={`${marker.actionId}-${marker.time}`} onClick={() => onMarkerClick(marker)} style={{ cursor: marker.actionId ? 'pointer' : 'default' }}>
                    <circle cx={point.x} cy={y(point.value)} r="5" fill={marker.status === 'BLOCKED' ? intelligencePalette.danger : intelligencePalette.text} stroke="rgba(6,9,18,0.98)" strokeWidth="2" />
                    <title>{marker.label}</title>
                  </g>
                );
              })}
            </svg>
          </div>
        </div>
      )}
    </section>
  );
}

function LifecyclePipeline({ lifecycle, selectedStage, onStageClick }) {
  const stages = Array.isArray(lifecycle.stages) ? lifecycle.stages : [];
  return (
    <section style={panel()}>
      <div style={{ fontSize: 19, fontWeight: 850, marginBottom: 6 }}>Intelligence-to-Action Lifecycle</div>
      <div style={{ color: intelligencePalette.muted, fontSize: 13, marginBottom: 16 }}>Detected → Story → Thesis → Proposed → Risk Checked → Approved → Executed → Monitored → Closed, with blocked and abstained exits visible.</div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(92px, 1fr))`, gap: 8, overflowX: 'auto', paddingBottom: 2 }}>
        {stages.map((row) => {
          const active = selectedStage === row.stage;
          const sideExit = ['BLOCKED', 'ABSTAINED', 'REJECTED'].includes(row.stage);
          return (
            <button
              key={row.stage}
              type="button"
              onClick={() => onStageClick(active ? '' : row.stage)}
              style={{
                border: `1px solid ${active ? intelligencePalette.borderStrong : sideExit ? 'rgba(255,122,138,0.28)' : intelligencePalette.border}`,
                background: active ? 'rgba(77,121,255,0.18)' : 'rgba(255,255,255,0.025)',
                color: intelligencePalette.text,
                borderRadius: 14,
                padding: 9,
                minHeight: 62,
                minWidth: 0,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ fontSize: 9, color: intelligencePalette.muted, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.stage.replace(/_/g, ' ')}</div>
              <div style={{ fontSize: 14, fontWeight: 850, marginTop: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.count ?? 0}</div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function AttributionTable({ attribution, groupBy, onGroupByChange }) {
  const [sortKey, setSortKey] = useState('pnlUsd');
  const [showAll, setShowAll] = useState(false);
  const rows = useMemo(() => {
    const list = Array.isArray(attribution.rows) ? attribution.rows.slice() : [];
    list.sort((a, b) => Number(b[sortKey] || 0) - Number(a[sortKey] || 0));
    return list;
  }, [attribution.rows, sortKey]);
  const visibleRows = showAll ? rows : rows.slice(0, 5);
  return (
    <section style={panel()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 850 }}>Top Winning / Losing Intelligence</div>
          <div style={{ color: intelligencePalette.muted, fontSize: 13 }}>Losing categories stay visible so the console remains truth-first.</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {GROUPS.map((group) => (
            <SignalChip key={group.value} label={group.label} tone={groupBy === group.value ? 'watch' : 'stable'} active={groupBy === group.value} onClick={() => onGroupByChange(group.value)} role="button" />
          ))}
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760 }}>
          <thead>
            <tr style={{ color: intelligencePalette.muted, fontSize: 12, textAlign: 'left' }}>
              {[
                ['name', 'Thesis / story type'],
                ['pnlUsd', 'P/L'],
                ['pnlPct', 'P/L %'],
                ['benchmarkDeltaPct', 'Benchmark delta'],
                ['actionCount', 'Actions'],
                ['winRatePct', 'Win rate'],
                ['avgHoldMinutes', 'Avg hold'],
                ['status', 'Status'],
              ].map(([key, label]) => (
                <th key={key} style={{ padding: '0 12px 10px 0' }}>
                  <button type="button" onClick={() => setSortKey(key)} style={headerButtonStyle}>{label}</button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={`${row.name}-${row.status}`} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <td style={cellStyle}>{row.name || '—'}</td>
                <td style={cellStyle}>{fmtUsd(row.pnlUsd)}</td>
                <td style={cellStyle}>{fmtPct(row.pnlPct)}</td>
                <td style={cellStyle}>{fmtPct(row.benchmarkDeltaPct)}</td>
                <td style={cellStyle}>{row.actionCount ?? '—'}</td>
                <td style={cellStyle}>{fmtPct(row.winRatePct)}</td>
                <td style={cellStyle}>{row.avgHoldMinutes ? `${Math.round(row.avgHoldMinutes)}m` : '—'}</td>
                <td style={cellStyle}><SignalChip label={row.status || '—'} tone={row.status === 'LOSER' ? 'decaying' : 'confirmed'} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 5 ? (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => setShowAll((value) => !value)}
            style={linkButtonStyle}
          >
            {showAll ? 'Show top 5' : `Show all ${rows.length}`}
          </button>
        </div>
      ) : null}
      {!rows.length ? <EmptyState label="No attribution rows yet." /> : null}
    </section>
  );
}

function BlockedActionsTable({ data, reasonFilter, onReasonFilter, selectedStage, navigate }) {
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const reasons = Array.from(new Set(rows.map((row) => row.reason).filter(Boolean))).sort();
  const filtered = rows.filter((row) => {
    if (reasonFilter && row.reason !== reasonFilter) return false;
    if (selectedStage === 'BLOCKED' && String(row.intendedAction).toUpperCase() === 'ABSTAIN') return false;
    if (selectedStage === 'ABSTAINED' && String(row.intendedAction).toUpperCase() !== 'ABSTAIN') return false;
    return true;
  });
  return (
    <section style={panel()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 850 }}>Blocked and Abstained Actions</div>
          <div style={{ color: intelligencePalette.muted, fontSize: 13 }}>Risk control and non-actions are first-class records.</div>
        </div>
        <select value={reasonFilter} onChange={(e) => onReasonFilter(e.target.value)} style={selectStyle}>
          <option value="">All reasons</option>
          {reasons.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
        </select>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 840 }}>
          <thead>
            <tr style={{ color: intelligencePalette.muted, fontSize: 12, textAlign: 'left' }}>
              {['Time', 'Token', 'Intended action', 'Agent', 'Thesis', 'Reason', 'Confidence', 'After', 'Detail'].map((label) => <th key={label} style={{ padding: '0 12px 10px 0' }}>{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.actionId || `${row.symbol}-${row.time}`} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <td style={cellStyle}>{fmtTime(row.time)}</td>
                <td style={cellStyle}>{row.symbol || row.tokenAddress || '—'}</td>
                <td style={cellStyle}><SignalChip label={row.intendedAction || '—'} tone={row.intendedAction} /></td>
                <td style={cellStyle}>{row.agent || '—'}</td>
                <td style={{ ...cellStyle, maxWidth: 260 }}>{row.thesis || '—'}</td>
                <td style={cellStyle}>{row.reason || 'UNKNOWN'}</td>
                <td style={cellStyle}>{row.confidence ?? '—'}</td>
                <td style={cellStyle}>{row.afterOutcome || 'Pending outcome tracking'}</td>
                <td style={cellStyle}>
                  <button type="button" onClick={() => navigate(row.detailUrl || '/agent-verdicts')} style={linkButtonStyle}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!filtered.length ? <EmptyState label="No blocked or abstained actions for this filter." /> : null}
    </section>
  );
}

function RecentActivityFeed({ data, selectedStage, navigate }) {
  const rows = (Array.isArray(data.rows) ? data.rows : []).filter((row) => !selectedStage || String(row.type || '').includes(selectedStage));
  return (
    <section style={panel()}>
      <div style={{ fontSize: 22, fontWeight: 850, marginBottom: 6 }}>Recent Agent Activity</div>
      <div style={{ color: intelligencePalette.muted, fontSize: 13, marginBottom: 16 }}>Latest reasoning and action lifecycle events.</div>
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.map((row) => (
          <button key={`${row.type}-${row.time}-${row.symbol}`} type="button" onClick={() => navigate(row.detailUrl || '/agent-verdicts')} style={{ ...panel({ textAlign: 'left', padding: 14 }), cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ color: intelligencePalette.muted, fontSize: 12 }}>{fmtTime(row.time)}</span>
                <SignalChip label={row.type || 'ACTION'} tone={row.type && row.type.includes('BLOCKED') ? 'decaying' : 'watch'} />
                <span style={{ color: intelligencePalette.text, fontWeight: 850 }}>{row.agent || 'Agent'} · {row.symbol || '—'}</span>
              </div>
              {row.confidence != null ? <span style={{ color: intelligencePalette.muted, fontSize: 12 }}>Confidence {row.confidence}</span> : null}
            </div>
            <div style={{ color: intelligencePalette.muted, fontSize: 13, lineHeight: 1.5 }}>{row.riskCheck || 'risk checked'}</div>
            {row.summary ? <div style={{ color: intelligencePalette.text, fontSize: 14, lineHeight: 1.5, marginTop: 6 }}>{row.summary}</div> : null}
          </button>
        ))}
      </div>
      {!rows.length ? <EmptyState label="No recent activity for this stage." /> : null}
    </section>
  );
}

function DrilldownLinks({ navigate }) {
  const links = [
    { label: 'Agent Trading Details', path: '/agent-verdicts' },
    { label: 'Decision Actions', path: '/actions' },
    { label: 'Theses', path: '/thesis' },
    { label: 'Stories', path: '/stories' },
    { label: 'Token Agents', path: '/agents' },
  ];
  return (
    <section style={panel()}>
      <div style={{ fontSize: 22, fontWeight: 850, marginBottom: 12 }}>Drill-downs</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {links.map((link) => (
          <button key={link.path} type="button" onClick={() => navigate(link.path)} style={linkButtonStyle}>{link.label} →</button>
        ))}
      </div>
    </section>
  );
}

export default function IntelligencePage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('30d');
  const [groupBy, setGroupBy] = useState('thesis');
  const [selectedStage, setSelectedStage] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState({
    summary: mockSummary,
    performance: emptyPerformancePayload('30d'),
    lifecycle: mockLifecycle,
    attribution: mockAttribution,
    blockedActions: mockBlockedActions,
    activity: mockActivity,
    demoMode: true,
  });

  useEffect(() => {
    logPageView();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchJson = async (path) => {
      const response = await fetch(`${SERVER_NAME}${path}`, { credentials: 'include' });
      if (!response.ok) throw new Error(`${path} failed (${response.status})`);
      return response.json();
    };
    async function load() {
      setLoading(true);
      try {
        const [summary, performance, lifecycle, attribution, blockedActions, activity] = await Promise.all([
          fetchJson(`/api/intelligence/summary?portfolioId=default&period=${encodeURIComponent(period)}`),
          fetchJson(`/api/intelligence/performance?portfolioId=default&period=${encodeURIComponent(period)}`),
          fetchJson(`/api/intelligence/lifecycle?portfolioId=default&period=${encodeURIComponent(period)}`),
          fetchJson(`/api/intelligence/attribution?portfolioId=default&period=${encodeURIComponent(period)}&groupBy=${encodeURIComponent(groupBy)}`),
          fetchJson(`/api/intelligence/blocked-actions?portfolioId=default&period=${encodeURIComponent(period)}`),
          fetchJson('/api/intelligence/activity?portfolioId=default&limit=25'),
        ]);
        if (cancelled) return;
        setPayload({ summary, performance, lifecycle, attribution, blockedActions, activity, demoMode: false });
      } catch (err) {
        if (cancelled) return;
        setPayload(buildFallbackPayload(period, groupBy));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [groupBy, period]);

  const handleMarkerClick = (marker) => {
    if (!marker || !marker.actionId) return;
    logEvent('intelligence_performance_marker_clicked', { action_id: marker.actionId });
    navigate(`/agent-verdicts?action_id=${encodeURIComponent(marker.actionId)}`);
  };

  return (
    <div style={{ minHeight: '100%', width: '100%', background: 'radial-gradient(circle at top, rgba(77, 121, 255, 0.18), transparent 34%), linear-gradient(180deg, rgba(10, 14, 30, 0.98) 0%, rgba(7, 10, 20, 1) 100%)', color: intelligencePalette.text }}>
      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '32px 20px 56px', boxSizing: 'border-box', textAlign: 'left' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 22 }}>
          <div>
            <div style={{ color: intelligencePalette.muted, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>
              E3D Intelligence Console
            </div>
            <h1 style={{ margin: 0, fontSize: 34, lineHeight: 1.12, letterSpacing: '-0.04em' }}>
              Truth-first cockpit for agent performance, risk control, and learning.
            </h1>
            <div style={{ color: intelligencePalette.muted, fontSize: 14, lineHeight: 1.6, marginTop: 10, maxWidth: 780 }}>
              On-chain event → story → thesis → agent reasoning → risk/admissibility check → action → outcome → attribution.
            </div>
          </div>
        </header>

        <div style={{ display: 'grid', gap: 18 }}>
          <TruthPanel summary={payload.summary} />
          <KpiGrid summary={payload.summary} period={period} navigate={navigate} />
          <PerformanceChart data={payload.performance} period={period} onPeriodChange={setPeriod} loading={loading} onMarkerClick={handleMarkerClick} />
          <LifecyclePipeline lifecycle={payload.lifecycle} selectedStage={selectedStage} onStageClick={setSelectedStage} />
          <AttributionTable attribution={payload.attribution} groupBy={groupBy} onGroupByChange={setGroupBy} />
          <BlockedActionsTable data={payload.blockedActions} reasonFilter={reasonFilter} onReasonFilter={setReasonFilter} selectedStage={selectedStage} navigate={navigate} />
          <RecentActivityFeed data={payload.activity} selectedStage={selectedStage} navigate={navigate} />
          <DrilldownLinks navigate={navigate} />
        </div>
      </div>
    </div>
  );
}

const cellStyle = {
  padding: '12px 12px 12px 0',
  color: intelligencePalette.text,
  fontSize: 13,
  verticalAlign: 'top',
};

const headerButtonStyle = {
  border: 0,
  background: 'transparent',
  color: intelligencePalette.muted,
  font: 'inherit',
  padding: 0,
  cursor: 'pointer',
  textAlign: 'left',
};

const linkButtonStyle = {
  border: `1px solid ${intelligencePalette.borderStrong}`,
  background: 'rgba(77,121,255,0.14)',
  color: intelligencePalette.text,
  borderRadius: 10,
  padding: '8px 12px',
  fontWeight: 800,
  fontSize: 12,
  cursor: 'pointer',
};

const selectStyle = {
  border: `1px solid ${intelligencePalette.border}`,
  background: '#0d1429',
  color: intelligencePalette.text,
  borderRadius: 10,
  padding: '8px 10px',
};
